import React from 'react';

interface UserData {
  phone: string;
  countryCode: string;
  testType: 'english' | 'math';
  testLanguage: 'english' | 'arabic';
  studyLocation: string;
  nationality: string;
  studyField: string;
  grade: string;
  schoolType: 'private' | 'government';
  schoolName: string;
}

interface StudentInfoStepProps {
  userData: UserData;
  setUserData: React.Dispatch<React.SetStateAction<UserData>>;
  onSubmit: () => void;
  isRTL: boolean;
}

const StudentInfoStep: React.FC<StudentInfoStepProps> = ({ 
  userData, 
  setUserData, 
  onSubmit, 
  isRTL 
}) => {
  const countries = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 
    'France', 'Netherlands', 'Switzerland', 'Sweden', 'Denmark', 'Norway'
  ];

  const nationalities = [
    'Kuwaiti', 'Saudi', 'Emirati', 'Qatari', 'Bahraini', 'Omani', 
    'Egyptian', 'Jordanian', 'Lebanese', 'Syrian', 'Palestinian', 'Other'
  ];

  const studyFields = [
    'Engineering', 'Medicine', 'Business', 'Computer Science', 'Law', 
    'Arts', 'Science', 'Education', 'Architecture', 'Pharmacy'
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid()) {
      onSubmit();
    }
  };

  const isFormValid = () => {
    return userData.studyLocation && userData.nationality && userData.studyField && 
           userData.grade && userData.schoolType && 
           (userData.schoolType === 'government' || userData.schoolName);
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <h2 className="fw-bold fs-5 text-center mb-4">
          {isRTL ? 'معلومات الطالب' : 'Student Information'}
        </h2>
        
        <form onSubmit={handleSubmit}>
          {/* Q3: Study Location */}
          <div className="mb-3">
            <label className="form-label fw-semibold">
              {isRTL ? 'أين تريد أن تدرس؟' : 'Where do you want to study?'}
            </label>
            <select 
              className="form-select"
              value={userData.studyLocation}
              onChange={(e) => setUserData(prev => ({ ...prev, studyLocation: e.target.value }))}
              required
            >
              <option value="">
                {isRTL ? 'اختر الدولة' : 'Select country'}
              </option>
              {countries.map(country => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          {/* Q4: Nationality */}
          <div className="mb-3">
            <label className="form-label fw-semibold">
              {isRTL ? 'الجنسية؟' : 'Nationality?'}
            </label>
            <select 
              className="form-select"
              value={userData.nationality}
              onChange={(e) => setUserData(prev => ({ ...prev, nationality: e.target.value }))}
              required
            >
              <option value="">
                {isRTL ? 'اختر الجنسية' : 'Select nationality'}
              </option>
              {nationalities.map(nationality => (
                <option key={nationality} value={nationality}>
                  {nationality}
                </option>
              ))}
            </select>
          </div>

          {/* Q5: Study Field */}
          <div className="mb-3">
            <label className="form-label fw-semibold">
              {isRTL ? 'ماذا تريد أن تدرس؟' : 'What do you want to study?'}
            </label>
            <div className="row g-2 mb-2">
              {studyFields.slice(0, 6).map(field => (
                <div key={field} className="col-6">
                  <button
                    type="button"
                    className={`btn btn-sm w-100 rounded-pill ${
                      userData.studyField === field 
                        ? 'btn-primary text-white' 
                        : 'btn-outline-secondary'
                    }`}
                    onClick={() => setUserData(prev => ({ ...prev, studyField: field }))}
                  >
                    {field}
                  </button>
                </div>
              ))}
            </div>
            <input
              type="text"
              className="form-control"
              placeholder={isRTL ? 'أو اكتب تخصصك' : 'Or type your field'}
              value={userData.studyField}
              onChange={(e) => setUserData(prev => ({ ...prev, studyField: e.target.value }))}
            />
          </div>

          {/* Q6: Grade */}
          <div className="mb-3">
            <label className="form-label fw-semibold">
              {isRTL ? 'الدرجة / الدرجة المتوقعة؟' : 'Grade / Expected Grade?'}
            </label>
            <input
              type="text"
              className="form-control"
              placeholder={isRTL ? 'مثال: 85%, A, 3.5 GPA' : 'e.g., 85%, A, 3.5 GPA'}
              value={userData.grade}
              onChange={(e) => setUserData(prev => ({ ...prev, grade: e.target.value }))}
              required
            />
          </div>

          {/* Q7: School Type */}
          <div className="mb-3">
            <label className="form-label fw-semibold">
              {isRTL ? 'مدرسة خاصة أم حكومية؟' : 'Private or Government school?'}
            </label>
            <div className="d-grid gap-2">
              <button
                type="button"
                className={`btn rounded-pill ${
                  userData.schoolType === 'private' 
                    ? 'btn-primary text-white' 
                    : 'btn-outline-secondary'
                }`}
                onClick={() => setUserData(prev => ({ ...prev, schoolType: 'private' }))}
              >
                {isRTL ? 'خاصة' : 'Private'}
              </button>
              <button
                type="button"
                className={`btn rounded-pill ${
                  userData.schoolType === 'government' 
                    ? 'btn-primary text-white' 
                    : 'btn-outline-secondary'
                }`}
                onClick={() => setUserData(prev => ({ ...prev, schoolType: 'government' }))}
              >
                {isRTL ? 'حكومية' : 'Government'}
              </button>
            </div>
          </div>

          {/* Q8: School Name (conditional) */}
          {userData.schoolType === 'private' && (
            <div className="mb-4">
              <label className="form-label fw-semibold">
                {isRTL ? 'أي مدرسة؟' : 'Which school?'}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={isRTL ? 'اكتب اسم المدرسة' : 'Enter school name'}
                value={userData.schoolName}
                onChange={(e) => setUserData(prev => ({ ...prev, schoolName: e.target.value }))}
                required
              />
            </div>
          )}

          <button 
            type="submit"
            className="btn btn-primary w-100 rounded-pill"
            disabled={!isFormValid()}
          >
            {isRTL ? 'متابعة إلى الاختبار' : 'Continue to Test'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StudentInfoStep;
